import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Pencil, Settings, Gauge, Bell, Zap } from 'lucide-react';
import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { SensorManager } from '../sensor-manager';

interface SettingsTabProps {
  device: any;
  onEditClick: () => void;
}

export function SettingsTab({ device, onEditClick }: SettingsTabProps) {
  // These would typically come from a settings API
  // Using useState for demo purposes
  const [settings, setSettings] = useState({
    enableAlerts: device.alert_enabled || true,
    enableAutoUpdates: false,
    powerSaveMode: false,
    dataCollectionInterval: 'medium', // 'low', 'medium', 'high'
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleSettingChange = (key: string, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSaveSettings = () => {
    // Here you would call an API to save the settings
    console.log('Saving settings:', settings);
    setHasUnsavedChanges(false);
    // Show success toast
  };

  return (
    <div className="space-y-6 pt-4">
      {/* Device Configuration Card */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-2">
          <Settings className="h-5 w-5" />
          <CardTitle>Device Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Device Name</p>
              <p className="font-medium">{device.name || 'Unnamed Device'}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Serial Number</p>
              <p className="font-mono text-sm">{device.serial_number}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">MAC Address</p>
              <p className="font-mono text-sm">{device.mac_address || 'Not set'}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Location</p>
              <p className="font-medium">{device.location_name || 'Not assigned'}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Device Status</p>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${device.status ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className="font-medium">{device.status ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Created</p>
              <p className="text-sm">{new Date(device.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          <Separator />

          <div className="flex justify-end">
            <Button variant="outline" onClick={onEditClick} className="gap-2">
              <Pencil className="h-4 w-4" />
              Edit Device Information
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sensor Management */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-2">
          <Gauge className="h-5 w-5" />
          <CardTitle>Sensor Management</CardTitle>
        </CardHeader>
        <CardContent>
          <SensorManager deviceId={device.id} deviceName={device.name} />
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-2">
          <Bell className="h-5 w-5" />
          <CardTitle>Notification Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-between items-center">
            <div className="space-y-1">
              <Label htmlFor="enable-alerts">Alert Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive notifications when sensors detect anomalies</p>
            </div>
            <Switch
              id="enable-alerts"
              checked={settings.enableAlerts}
              onCheckedChange={(checked) => handleSettingChange('enableAlerts', checked)}
            />
          </div>

          <div className="flex justify-between items-center">
            <div className="space-y-1">
              <Label htmlFor="enable-auto-updates">Automatic Updates</Label>
              <p className="text-sm text-muted-foreground">Automatically install firmware updates when available</p>
            </div>
            <Switch
              id="enable-auto-updates"
              checked={settings.enableAutoUpdates}
              onCheckedChange={(checked) => handleSettingChange('enableAutoUpdates', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Power & Performance Settings */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-2">
          <Zap className="h-5 w-5" />
          <CardTitle>Power & Performance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-between items-center">
            <div className="space-y-1">
              <Label htmlFor="power-save-mode">Power Save Mode</Label>
              <p className="text-sm text-muted-foreground">
                Reduce power consumption with less frequent sensor readings
              </p>
            </div>
            <Switch
              id="power-save-mode"
              checked={settings.powerSaveMode}
              onCheckedChange={(checked) => handleSettingChange('powerSaveMode', checked)}
            />
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="data-collection-interval">Data Collection Interval</Label>
              <p className="text-sm text-muted-foreground">
                How frequently the device collects and transmits sensor data
              </p>
            </div>
            <Select
              value={settings.dataCollectionInterval}
              onValueChange={(value) => handleSettingChange('dataCollectionInterval', value)}
            >
              <SelectTrigger id="data-collection-interval">
                <SelectValue placeholder="Select interval" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low Frequency (Every 30 minutes)</SelectItem>
                <SelectItem value="medium">Medium Frequency (Every 5 minutes)</SelectItem>
                <SelectItem value="high">High Frequency (Every minute)</SelectItem>
                <SelectItem value="realtime">Real-time (Every 10 seconds)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Save Settings */}
      <div className="flex justify-end gap-2">
        {hasUnsavedChanges && (
          <p className="text-sm text-muted-foreground flex items-center">You have unsaved changes</p>
        )}
        <Button onClick={handleSaveSettings} disabled={!hasUnsavedChanges} className="gap-2">
          <Settings className="h-4 w-4" />
          {hasUnsavedChanges ? 'Save Settings' : 'Settings Saved'}
        </Button>
      </div>
    </div>
  );
}
